import { Box, Grid, IconButton, Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import EmailIcon from "@material-ui/icons/Email";
import DownloadIcon from "@material-ui/icons/GetApp";
import MobileIcon from "@material-ui/icons/PhoneAndroid";
import SendIcon from "@material-ui/icons/Send";
import { useState } from "react";
import * as yup from "yup";
import { AdviceStatuses, AmlStatuses, ApplicationClientDto, ApplicationClientRoles, ClientDto, ClientStatuses } from "../../../api/apiClients";
import { getClientDisplayName } from "../../../common/helpers";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  exportKiwiSaverApplicationCsv,
  getAdviceAgreementPDFAsync,
  getApplicationPdf,
  getClientAsync,
  getSavingsAdviceToolPdf,
  getVerificationPDFAsync,
  resendEmailInvitationAsync,
  resendSmsInvitationAsync,
  updateApplicantEmailMobileAsync,
} from "../../../store/adviser";
import FormikWrapper from "../../containers/FormikWrapper";
import { Colors, DialogBox, SubtitleText } from "../../controls/helpers";
import { NumberTextInput, TextInput } from "../../controls/inputs";
import { getAllApplicants } from "../Helpers";
import { ApplicantRoleChip } from "../IconsAndChips";
import { Details, Section } from "../Section";

enum FormMode {
  None,
  ConfirmSendEmail,
  ConfirmSendSms,
  ConfirmUpdate,
}

const Applicant = ({ client, primaryClient }: { client: ApplicationClientDto; primaryClient?: ClientDto }) => {
  const { isLoading, isSubmitting } = useAppSelector((state) => state.adviser);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
  const dispatch = useAppDispatch();

  const downloadProgressPdf = (isSavingsAdviceTool: boolean) => {
    if (!isSavingsAdviceTool && client.clientStatus === ClientStatuses.Completed) dispatch(getApplicationPdf(client.clientId!));
    else if (isSavingsAdviceTool && client.adviceStatus === AdviceStatuses.Completed) dispatch(getSavingsAdviceToolPdf(client.clientId!));
  };

  return (
    <Grid item xs={12}>
      <Section hideTopBorder>
        <Details>
          <SubtitleText black hideMarginBottom>
            <b>{getClientDisplayName(client)}</b>
          </SubtitleText>
          {(client.clientStatus == ClientStatuses.None || client.clientStatus == ClientStatuses.Invited) && (
            <Tooltip title="Update the applicant's email and/or mobile" placement="bottom">
              <Box component="div" display="block" marginLeft={1}>
                <IconButton onClick={() => setFormMode(FormMode.ConfirmUpdate)} size="small">
                  <EditIcon fontSize="small" htmlColor={Colors.Blue} />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          <Box flexGrow={1} textAlign="right">
            <ApplicantRoleChip title={client.role === ApplicationClientRoles.Primary ? "Primary" : "Secondary"} />
          </Box>
        </Details>
      </Section>
      {client.role === ApplicationClientRoles.Primary && client.adviceStatus !== AdviceStatuses.Direct && client.adviceStatus !== AdviceStatuses.Skipped && (
        <Section hideBottomBorder hideTopBorder>
          <Details title="Savings Advice Tool">
            {client.adviceStatus !== AdviceStatuses.Completed && (
              <Tooltip title="Resend email">
                <Box display="inline">
                  <IconButton onClick={() => setFormMode(FormMode.ConfirmSendEmail)} size="small">
                    <EmailIcon htmlColor={Colors.Blue} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
            {client.mobile && client.role === ApplicationClientRoles.Primary && client.adviceStatus !== AdviceStatuses.Completed && (
              <Tooltip title="Resend SMS">
                <Box display="inline">
                  <IconButton onClick={() => setFormMode(FormMode.ConfirmSendSms)} size="small">
                    <MobileIcon htmlColor={Colors.Blue} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
            <Tooltip title="Download Savings Advice Tool">
              <Box>
                <IconButton disabled={client.adviceStatus !== AdviceStatuses.Completed} onClick={() => downloadProgressPdf(true)} size="small">
                  <DownloadIcon htmlColor={client.adviceStatus !== AdviceStatuses.Completed ? "" : Colors.Blue} />
                </IconButton>
              </Box>
            </Tooltip>
          </Details>
        </Section>
      )}
      <Section hideBottomBorder hideTopBorder>
        <Details title="Application">
          {(client.role !== ApplicationClientRoles.Joint || primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Successful)) &&
            client.clientStatus !== ClientStatuses.Completed &&
            (client.adviceStatus === AdviceStatuses.Completed || client.adviceStatus === AdviceStatuses.Skipped || client.adviceStatus === AdviceStatuses.Direct) && (
              <Tooltip title="Resend email">
                <Box display="inline">
                  <IconButton onClick={() => setFormMode(FormMode.ConfirmSendEmail)} size="small">
                    <EmailIcon htmlColor={Colors.Blue} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          {client.mobile &&
            client.role === ApplicationClientRoles.Primary &&
            client.clientStatus !== ClientStatuses.Completed &&
            (client.adviceStatus === AdviceStatuses.Completed || client.adviceStatus === AdviceStatuses.Skipped) && (
              <Tooltip title="Resend SMS">
                <Box display="inline">
                  <IconButton onClick={() => setFormMode(FormMode.ConfirmSendSms)} size="small">
                    <MobileIcon htmlColor={Colors.Blue} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          <Tooltip title="Download application">
            <Box>
              <IconButton disabled={client.clientStatus !== ClientStatuses.Completed} onClick={() => downloadProgressPdf(false)} size="small">
                <DownloadIcon htmlColor={client.clientStatus === ClientStatuses.Completed ? Colors.Blue : ""} />
              </IconButton>
            </Box>
          </Tooltip>
        </Details>
      </Section>
      {!!primaryClient?.kiwiSaverApplication?.agreementVersion && !!primaryClient?.applicationCompleted && (
        <Section hideTopBorder hideBottomBorder>
          <Details title="Advice Agreement">
            <Tooltip title="Download Advice Agreement">
              <Box component="div" display="block">
                <IconButton
                    disabled={!primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Successful) && !primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Failed)}
                    onClick={() => dispatch(getAdviceAgreementPDFAsync(client.clientId!))} size="small">
                  <DownloadIcon htmlColor={!primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Successful) &&
                        !primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Failed) ? undefined : Colors.Blue} />
                </IconButton>
              </Box>
            </Tooltip>
          </Details>
        </Section>
      )}
      {!!primaryClient?.applicationCompleted && !!primaryClient.kiwiSaverApplication?.isRetention && (
        <Section hideTopBorder hideBottomBorder>
          <Details title="Application extract">
            <Tooltip title="Export application">
              <Box component="div" display="block">
                <IconButton size="small" onClick={() => dispatch(exportKiwiSaverApplicationCsv(client.clientId!))}>
                  <DownloadIcon htmlColor={Colors.Blue} />
                </IconButton>
              </Box>
            </Tooltip>
          </Details>
        </Section>
      )}
      {!primaryClient?.kiwiSaverApplication?.isRetention && (
        <Section hideTopBorder hideBottomBorder>
          <Details title="Identity verification">
            <Tooltip title="Download identity verification">
              <Box component="div" display="block">
                <IconButton
                  disabled={(!primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Successful) &&
                      !primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Failed)) ||
                      !(client.hasCloudcheckReference || client.hasLiveVerificationReference)}
                  onClick={() => dispatch(getVerificationPDFAsync(client.clientId!))}
                  size="small"
                >
                  <DownloadIcon htmlColor={(!primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Successful) &&
                      !primaryClient?.amlStatus?.split(", ").includes(AmlStatuses.Failed)) ||
                      !(client.hasCloudcheckReference || client.hasLiveVerificationReference) ? "" : Colors.Blue} />
                </IconButton>
              </Box>
            </Tooltip>
          </Details>
          {client.role === ApplicationClientRoles.Primary && <br />}
        </Section>
      )}
      <DialogBox
        open={formMode === FormMode.ConfirmSendEmail || formMode === FormMode.ConfirmSendSms}
        icon={<SendIcon htmlColor={Colors.Green} />}
        ok={() => {
          setFormMode(FormMode.None);
          dispatch(formMode === FormMode.ConfirmSendEmail ? resendEmailInvitationAsync({ clientId: client.clientId }) : resendSmsInvitationAsync({ clientId: client.clientId }));
        }}
        cancel={() => setFormMode(FormMode.None)}
        isSubmitting={isSubmitting}
      >
        <SubtitleText black hideMarginBottom>
          Are you sure you want to send an {formMode === FormMode.ConfirmSendEmail ? "email" : "SMS"} to {formMode === FormMode.ConfirmSendEmail ? client.email : client.mobile}?
        </SubtitleText>
      </DialogBox>
      <FormikWrapper
        initialValues={{ clientId: client.clientId, email: client.email, mobile: client.mobile }}
        validationSchema={updateEmailMobileValidationSchema(primaryClient)}
        onSubmit={async (values) => {
          setFormMode(FormMode.None);
          await dispatch(updateApplicantEmailMobileAsync(values));
          dispatch(getClientAsync(primaryClient?.clientId!));
        }}
      >
        {({ handleSubmit }) => (
          <DialogBox
            open={formMode === FormMode.ConfirmUpdate}
            icon={<EmailIcon htmlColor={Colors.Green} />}
            ok={handleSubmit}
            cancel={() => setFormMode(FormMode.None)}
            isSubmitting={isLoading || isSubmitting}
          >
            <SubtitleText black>Update the applicant's email and/or mobile below.</SubtitleText>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextInput name="email" label="Email" />
              </Grid>
              <Grid item xs={12}>
                <NumberTextInput label="Mobile" name="mobile" mask="999999999999" setStringValue />
              </Grid>
            </Grid>
          </DialogBox>
        )}
      </FormikWrapper>
    </Grid>
  );
};

const updateEmailMobileValidationSchema = (primaryClient?: ClientDto) =>
  yup
    .object({
      email: yup.string().email("Email is invalid").required("Email is required"),
      mobile: yup.string(),
      clientId: yup.string(),
    })
    .test("Applicant email", "Duplicate applicant email", function (values) {
      const applicants = getAllApplicants(primaryClient);
      const hasDuplicatedEmail = applicants.some((client) => values.clientId !== client.clientId && values.email === client.email?.toLowerCase());
      return !hasDuplicatedEmail || this?.createError({ path: "email", message: "Applicant must have a unique email" });
    });

export default Applicant;
